// ============================================================================
// WeeklyGoalProgress — THIS WEEK's coach goal as a progress checklist (Wave 3).
// ----------------------------------------------------------------------------
// The client dashboard card for the trainer-set weekly goal. Reads
// GET /api/client/me/weekly-goal ({week_start, goal, progress}) and renders:
//   • nutrition targets — kcal/protein/carbs/fat/water as labelled progress
//     bars (TODAY's logged intake vs the resolved coach∨auto target), reusing
//     the NutritionTargetsCard bar style.
//   • exercise homework — each {type, count} as a 'Strength 2/3'-style counter
//     (completed vs target) with a small done/remaining badge.
// When the coach hasn't set a goal this week, a gentle empty state shows instead.
// All LABELS come from i18n keys (en + 繁中); the only server text rendered is
// the coach's own free-text notes (client-authored, safe per backend contract).
// Mobile-first, Pulse, reduced-motion friendly (CSS-only bar fills).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge, Banner, Button, Card, CardHeader } from "../../components";
import { useClientWeeklyGoal } from "../hooks";
import { fmtDate, fmtNum } from "./portalLib";
import type {
  WeeklyGoalExerciseProgress,
  WeeklyGoalTargets,
  WeeklyGoalTotals,
} from "../../api";

type Accent = "coral" | "violet" | "ocean" | "lime" | "amber";

const BAR: Record<Accent, string> = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
  amber: "bg-grad-amber",
};

/** A single nutrition target as a labelled progress bar (logged vs target). */
function ProgressRow({
  label,
  logged,
  target,
  unit,
  accent,
}: {
  label: string;
  logged: number;
  target: number | null;
  unit: string;
  accent: Accent;
}) {
  const { t } = useTranslation("weeklyGoals");
  const pct =
    target && target > 0 ? Math.min(100, Math.round((logged / target) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="font-display text-sm font-bold text-body">{label}</span>
        <span className="text-sm tabular-nums text-body-soft">
          {target != null
            ? t("targets.loggedOfTarget", {
                logged: fmtNum(logged, 0),
                target: fmtNum(target, 0),
                unit,
              })
            : `${fmtNum(logged, 0)} ${unit}`}
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-sunken"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-pill ${BAR[accent]} transition-[width] duration-500 ease-kinetic`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Localized label for an open-ended exercise `type` (falls back to the raw). */
function useExerciseTypeLabel() {
  const { t } = useTranslation("weeklyGoals");
  return (type: string): string => {
    const key = (type || "").toLowerCase();
    const known = ["strength", "cardio", "mobility", "hiit", "mixed"];
    return known.includes(key) ? t(`exercise.type.${key}`) : type || t("exercise.title");
  };
}

/** One exercise homework counter row — 'Strength 2/3' + a done/remaining badge. */
function ExerciseRow({ item }: { item: WeeklyGoalExerciseProgress }) {
  const { t } = useTranslation("weeklyGoals");
  const typeLabel = useExerciseTypeLabel()(item.type);
  const target = Math.max(0, item.count);
  const completed = Math.max(0, item.completed);
  const isDone = target > 0 && completed >= target;
  const remaining = Math.max(0, target - completed);
  const pct = target > 0 ? Math.min(100, Math.round((completed / target) * 100)) : 0;
  return (
    <div className="rounded-md bg-surface-sunken px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-sm font-bold text-body">{typeLabel}</span>
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-extrabold tabular-nums text-body">
            {t("exercise.completedOf", { completed, count: target })}
          </span>
          <Badge tone={isDone ? "success" : "neutral"} dot className="shrink-0">
            {isDone ? t("exercise.done") : t("exercise.remaining", { remaining })}
          </Badge>
        </div>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-pill bg-surface"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeLabel}
      >
        <div
          className="h-full rounded-pill bg-grad-lime transition-[width] duration-500 ease-kinetic"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function WeeklyGoalProgress() {
  const { t } = useTranslation("weeklyGoals");
  const query = useClientWeeklyGoal();

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader title={t("progress.title")} />
        <div className="space-y-4" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-7 animate-pulse rounded-md bg-surface-sunken" />
          ))}
        </div>
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card className="space-y-3">
        <CardHeader title={t("progress.title")} />
        <Banner tone="warning" title={t("progress.error")} />
        <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
          {t("progress.retry")}
        </Button>
      </Card>
    );
  }

  const { week_start: weekStart, goal, progress } = query.data;

  // Gentle empty state when the coach hasn't set a goal for this week.
  if (!goal || !progress.has_goal) {
    return (
      <Card className="space-y-2">
        <CardHeader
          title={t("progress.title")}
          subtitle={t("weekOf", { date: fmtDate(weekStart) })}
        />
        <div className="rounded-md bg-surface-sunken px-4 py-6 text-center">
          <p className="font-display font-bold text-body">{t("progress.empty")}</p>
          <p className="mt-1 text-sm text-body-soft">{t("progress.emptyHint")}</p>
        </div>
      </Card>
    );
  }

  const targets: WeeklyGoalTargets = progress.targets;
  const today: WeeklyGoalTotals = progress.today;
  const exercise = progress.exercise ?? [];
  const macrosMissing = targets.kcal == null && targets.protein_g == null;
  const allExerciseDone =
    exercise.length > 0 &&
    exercise.every((e) => e.count > 0 && e.completed >= e.count);

  return (
    <Card className="space-y-4">
      <CardHeader
        title={t("progress.title")}
        subtitle={`${t("weekOf", { date: fmtDate(weekStart) })} · ${t("setByCoach")}`}
      />

      {/* --- Nutrition targets (today's intake vs the coach target) --- */}
      <div>
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("targets.title")}
        </p>
        {macrosMissing ? (
          <Banner tone="info" title={t("progress.needStats")} />
        ) : (
          <div className="space-y-4">
            <ProgressRow
              label={t("targets.kcal")}
              logged={today.kcal}
              target={targets.kcal}
              unit={t("targets.kcalUnit")}
              accent="coral"
            />
            <ProgressRow
              label={t("targets.protein")}
              logged={today.protein_g}
              target={targets.protein_g}
              unit={t("targets.gUnit")}
              accent="violet"
            />
            <ProgressRow
              label={t("targets.carbs")}
              logged={today.carbs_g}
              target={targets.carbs_g}
              unit={t("targets.gUnit")}
              accent="amber"
            />
            <ProgressRow
              label={t("targets.fat")}
              logged={today.fat_g}
              target={targets.fat_g}
              unit={t("targets.gUnit")}
              accent="lime"
            />
            <ProgressRow
              label={t("targets.water")}
              logged={today.water_ml}
              target={targets.water_ml}
              unit={t("targets.mlUnit")}
              accent="ocean"
            />
          </div>
        )}
        <p className="mt-2 text-xs text-body-muted">{t("progress.todayHint")}</p>
      </div>

      {/* --- Exercise homework counters (completed vs target this week) --- */}
      <div>
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("exercise.title")}
        </p>
        {exercise.length === 0 ? (
          <p className="rounded-md bg-surface-sunken px-4 py-4 text-center text-sm text-body-soft">
            {t("exercise.none")}
          </p>
        ) : (
          <div className="space-y-2">
            {exercise.map((item, i) => (
              <ExerciseRow key={`${item.type}-${i}`} item={item} />
            ))}
            {allExerciseDone && (
              <Banner tone="success" title={t("exercise.allComplete")} />
            )}
          </div>
        )}
      </div>

      {/* --- Coach's notes (the only server text we render — client-authored) --- */}
      {goal.notes && goal.notes.trim() !== "" && (
        <div className="rounded-md bg-lime-soft px-3 py-2.5">
          <p className="mb-0.5 font-display text-xs font-bold uppercase tracking-kicker text-lime-600">
            {t("notesLabel")}
          </p>
          <p className="text-sm leading-relaxed text-body">{goal.notes}</p>
        </div>
      )}
    </Card>
  );
}
